/**
 * Shared auth-aware fetch wrapper for application services that hit
 * `api.eriol.io` directly (AlertService, OverlayService, PanelService,
 * SceneTemplateService, etc).
 *
 * Before this helper existed, each of those services rolled its own
 * `buildHeaders` + raw `fetch()`, all reading `localStorage` for the
 * accessKey. The duplication meant we couldn't migrate them to the
 * HttpOnly cookie auth flow without touching ~20 callsites.
 *
 * Behaviour matches `services/api.ts`:
 *  - Browser context (app.eriol.io in a regular browser): rely entirely
 *    on the `eriol_session` HttpOnly cookie set by /auth/login. No
 *    Authorization header is added. `credentials:"include"` ships the
 *    cookie. For mutating requests we also attach `X-CSRF-Token` read
 *    from the non-HttpOnly `eriol_csrf` cookie.
 *  - Electron context: an accessKey arrives via custom-protocol deep
 *    link from the web SPA and gets persisted to localStorage; cookies
 *    can't bridge the cross-process handoff, so we keep sending it as
 *    the Authorization header.
 *
 * RomService is NOT a consumer — it talks to the localhost extension
 * agent, which is a separate trust boundary.
 *
 * `buildAuthHeaders` is the ONE place that builds the headers of an
 * authenticated backend request, desktop tracking headers included
 * (`X-Eriol-Version` / `X-Eriol-Platform` / `X-Eriol-Observed-At`):
 * services/api.ts, authFetch, the donation services and the signaling ticket
 * all go through it. It is async because the desktop version comes from the
 * main process over IPC: the requests fired at startup wait for it (briefly,
 * see DESKTOP_VERSION_WAIT_MS) instead of going out without it, and being a
 * Promise makes the compiler reject any caller that forgets to wait.
 *
 * Los endpoints públicos que pasan por acá (`/designs`, `/designs/:slug`,
 * `/scene-templates`: `@Get()` sin `@UseGuards(AccessKeyGuard)`) NO esperan:
 * el backend anota la versión sólo cuando la request autentica, así que ahí
 * la espera era demora sin dato a cambio. Se piden con
 * `{ publicEndpoint: true }`, igual que `api.get` sin autenticar.
 */

import {
  getDesktopClientHeaders,
  peekDesktopClientHeaders,
} from "../services/desktopClientHeaders";

const CLIENT_APP = "desktop";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const IS_ELECTRON =
  typeof window !== "undefined" && Boolean(window.electronAPI);

function getElectronAccessKey(): string | null {
  if (!IS_ELECTRON) return null;
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem("@Eriol/system/accessKey");
}

function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)eriol_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export interface BuildAuthHeadersOptions {
  /** Defaults to true. Set false when sending FormData (browser sets the boundary). */
  json?: boolean;
  /**
   * El endpoint NO autentica: el backend anota la versión sólo en las
   * requests que pasan por AccessKeyGuard, así que esperar al IPC acá es
   * demora sin dato a cambio. Va la versión si ya se conoce, nada si no.
   *
   * Sólo para endpoints públicos de verdad. En uno autenticado, la cuenta
   * dejaría de reportar su versión por ese camino (lo fija
   * `backendClientHeaders.test.ts`, que corre cada servicio de verdad y mira
   * lo que salió por la red).
   */
  publicEndpoint?: boolean;
}

export async function buildAuthHeaders(
  method: string,
  options: BuildAuthHeadersOptions = {},
): Promise<Record<string, string>> {
  const { json = true, publicEndpoint = false } = options;
  const headers: Record<string, string> = {
    "X-Client-App": CLIENT_APP,
    // Desktop version/platform: the backend records them on every request
    // that authenticates, so every authenticated path must carry them.
    ...(publicEndpoint
      ? peekDesktopClientHeaders()
      : await getDesktopClientHeaders()),
  };
  if (json) headers["Content-Type"] = "application/json";

  const electronKey = getElectronAccessKey();
  if (electronKey) {
    headers["Authorization"] = `AccessKey ${electronKey}`;
  } else if (!SAFE_METHODS.has(method.toUpperCase())) {
    const csrf = readCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  return headers;
}

/**
 * Convenience wrapper around `fetch` that:
 *  - Sets `credentials: "include"` so the session cookie rides along.
 *  - Merges the auth headers from `buildAuthHeaders` with any caller-provided
 *    headers (caller wins on conflict).
 *
 * Use this for services that previously did `fetch(url, { headers: buildHeaders(true) })`.
 * Multipart uploads should call `buildAuthHeaders(method, { json: false })`
 * themselves and pass the result through `init.headers`.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit & { method?: string; publicEndpoint?: boolean } = {},
): Promise<Response> {
  const { publicEndpoint, ...fetchInit } = init;
  const method = init.method ?? "GET";
  const baseHeaders = await buildAuthHeaders(method, { publicEndpoint });
  const merged: Record<string, string> = { ...baseHeaders };
  if (init.headers) {
    const provided = new Headers(init.headers as HeadersInit);
    provided.forEach((value, key) => {
      merged[key] = value;
    });
  }

  // `fetchInit`, sin `publicEndpoint`: es una opción nuestra y no de fetch.
  return fetch(input, {
    ...fetchInit,
    method,
    headers: merged,
    credentials: "include",
  });
}
